import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Database, MapPin, Layers } from "lucide-react";

export default function StatsPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["/api/stats"],
    queryFn: async () => {
      const response = await fetch("/api/stats");
      if (!response.ok) throw new Error("Erro ao buscar estatisticas");
      return response.json();
    },
  });

  const { data: uploads } = useQuery({
    queryKey: ["/api/uploads"],
    queryFn: async () => {
      const response = await fetch("/api/uploads");
      if (!response.ok) return [];
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12 text-muted-foreground">
          Carregando estatisticas...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" data-testid="text-page-title">Estatisticas</h2>
          <p className="text-muted-foreground">Visao geral dos dados cadastrados</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total de Entidades</CardTitle>
            <Database className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-entities">
              {stats?.totalEntities?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Registros cadastrados no sistema
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Tipos de Entidades</CardTitle>
            <Layers className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-types">
              {stats?.byType?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Categorias diferentes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Estados Cobertos</CardTitle>
            <MapPin className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-states">
              {stats?.topStates?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              UFs com registros
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Entidades por Tipo</CardTitle>
          <CardDescription>Distribuicao por categoria de entidade</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.byType && stats.byType.length > 0 ? (
            <div className="space-y-3">
              {stats.byType.map((item: any) => (
                <div key={item.entityType} className="flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium capitalize">
                        {item.entityType || "Nao especificado"}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {parseInt(item.count).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full"
                        style={{
                          width: `${Math.max(2, (parseInt(item.count) / (stats.totalEntities || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum dado disponivel
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top 10 Estados</CardTitle>
          <CardDescription>Estados com mais registros cadastrados</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.topStates && stats.topStates.length > 0 ? (
            <div className="space-y-3">
              {stats.topStates.map((item: any, index: number) => (
                <div key={item.state} className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center mr-3 font-semibold text-sm text-orange-400">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{item.state}</span>
                      <span className="text-sm text-muted-foreground">
                        {parseInt(item.count).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full"
                        style={{
                          width: `${Math.max(2, (parseInt(item.count) / (stats.totalEntities || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum dado disponivel
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Historico de Uploads</CardTitle>
          <CardDescription>Ultimas importacoes de arquivos Excel</CardDescription>
        </CardHeader>
        <CardContent>
          {Array.isArray(uploads) && uploads.length > 0 ? (
            <div className="space-y-3">
              {uploads.slice(0, 10).map((upload: any) => (
                <div
                  key={upload.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  data-testid={`upload-history-${upload.id}`}
                >
                  <div className="flex-1">
                    <div className="font-medium">{upload.fileName}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(upload.uploadedAt).toLocaleString("pt-BR")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-green-700">
                      {upload.recordsImported} importados
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {upload.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum upload realizado ainda
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
